import { ReactNode } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import { Avatar, Collapsible, Icon, Stack, useToggle } from '@openedx/paragon';
import { KeyboardArrowDown, KeyboardArrowUp } from '@openedx/paragon/icons';
import { useIntl } from '@edx/frontend-platform/i18n';

import { useLibraryPublishHistoryEntries } from '@src/library-authoring/data/apiHooks';
import { LoadingSpinner } from '@src/generic/Loading';

import { LibraryHistoryEntry, LibraryPublishHistoryGroup } from '../../data/api';
import messages from './messages';
import { getItemIcon } from '@src/generic/block-type-utils';
import { ContributorsAvatars } from './ContributorAvatars';
import { HistoryLogGroupEntries } from './HistoryLogGroupEntries';

export interface HistoryLogGroupTitleProps {
  titleMessage: string | ReactNode;
  dateMessage: string;
  disableCollapsible?: boolean;
}

export interface HistoryCreatedLogGroupProps {
  user?: string | null;
  displayName: string;
  itemType: string;
  createdAt: string;
  // When true, renders a vertical connector line below the creation entry to link it to publish groups that predate the container's creation.
  showLogVert?: boolean;
}

export interface HistoryDraftLogGroupProps {
  itemKey: string;
  displayName: string;
  entries: LibraryHistoryEntry[];
}

export interface HistoryPublishLogGroupProps extends LibraryPublishHistoryGroup {
  itemKey: string;
  // When true, hides the vertical connector line rendered below this group. Used for the last group
  // in the history log to avoid a dangling connector with nothing below it.
  hideLogVert?: boolean;
}

const HistoryLogGroupTitle = ({
  titleMessage,
  dateMessage,
  disableCollapsible = false,
}: HistoryLogGroupTitleProps) => {
  return (
    <Stack direction="horizontal" gap={2} className="mb-1">
      <Avatar className="history-log-group-avatar big-avatar" size="md" />
      <Stack>
        <Stack direction="horizontal" gap={1}>
          {titleMessage}
        </Stack>
        <span className="small text-gray-500">
          {dateMessage}
        </span>
      </Stack>
      {!disableCollapsible && (
        <>
          <Collapsible.Visible whenClosed>
            <Icon src={KeyboardArrowDown} />
          </Collapsible.Visible>
          <Collapsible.Visible whenOpen>
            <Icon src={KeyboardArrowUp} />
          </Collapsible.Visible>
        </>
      )}
    </Stack>
  );
};

export const HistoryCreatedLogGroup = ({
  user,
  displayName,
  itemType,
  createdAt,
  showLogVert,
}: HistoryCreatedLogGroupProps) => {
  const intl = useIntl();

  return (
    <div className="history-log-group publish-group">
      <HistoryLogGroupTitle
        titleMessage={intl.formatMessage(messages.createdTitle, {
          user: user ?? intl.formatMessage(messages.historyEntryDefaultUser),
          displayName: <span className="history-log-title text-truncate">{displayName}</span>,
          icon: <Icon src={getItemIcon(itemType)} />,
        })}
        dateMessage={moment(createdAt).fromNow()}
        disableCollapsible
      />
      {showLogVert && <div className="history-log-vert" />}
    </div>
  );
};

export const HistoryDraftLogGroup = ({
  itemKey,
  displayName,
  entries,
}: HistoryDraftLogGroupProps) => {
  const intl = useIntl();

  return (
    <div className="history-log-group draft-group">
      <Collapsible.Advanced>
        <Collapsible.Trigger>
          <HistoryLogGroupTitle
            titleMessage={intl.formatMessage(
              messages.draftTitle,
              {
                displayName: <span className="history-log-title text-truncate">{displayName}</span>,
              },
            )}
            dateMessage={intl.formatMessage(
              messages.draftTitleDate,
              {
                count: entries.length,
                date: moment(entries?.at(-1)?.changedAt ?? '').fromNow(),
              },
            )}
          />
        </Collapsible.Trigger>
        <Collapsible.Body>
          <HistoryLogGroupEntries itemKey={itemKey} entries={entries} />
        </Collapsible.Body>
      </Collapsible.Advanced>
      <div className="history-log-vert" />
    </div>
  );
};

export const HistoryPublishLogGroup = ({
  itemKey,
  publishLogUuid,
  directPublishedEntities,
  publishedBy,
  publishedAt,
  contributors,
  hideLogVert,
}: HistoryPublishLogGroupProps) => {
  const intl = useIntl();
  const [isOpenCollapsible, openCollapsible, closeCollapsible] = useToggle(false);

  const {
    data: entries,
    isPending,
  } = useLibraryPublishHistoryEntries(itemKey, publishLogUuid, isOpenCollapsible);

  const dateMessage = moment(publishedAt).fromNow();
  const hasContributors = contributors.length > 0;

  const titleMessage = directPublishedEntities.length === 1
    ? intl.formatMessage(messages.publishTitle, {
      user: publishedBy || intl.formatMessage(messages.historyEntryDefaultUser),
      displayName: <span className="history-log-title text-truncate">{directPublishedEntities[0].title}</span>,
      icon: <Icon src={getItemIcon(directPublishedEntities[0].entityType)} />,
    })
    : intl.formatMessage(messages.publishTitleMultiple, {
      user: publishedBy || intl.formatMessage(messages.historyEntryDefaultUser),
      icon: <Icon src={getItemIcon('default')} />,
    });

  return (
    <div className="history-log-group publish-group">
      {hasContributors ?
        (
          <Collapsible.Advanced
            open={isOpenCollapsible}
            onOpen={openCollapsible}
            onClose={closeCollapsible}
          >
            <Collapsible.Trigger>
              <HistoryLogGroupTitle titleMessage={titleMessage} dateMessage={dateMessage} />
            </Collapsible.Trigger>
            <Collapsible.Body>
              {isPending ?
                (
                  <>
                    <div className="history-log-vert" />
                    <div className="ml-2 mt-2 w-100">
                      <LoadingSpinner />
                    </div>
                  </>
                ) :
                <HistoryLogGroupEntries entries={entries ?? []} itemKey={itemKey} />}
            </Collapsible.Body>
          </Collapsible.Advanced>
        ) :
        (
          <>
            {!hideLogVert && (
              <HistoryLogGroupTitle titleMessage={titleMessage} dateMessage={dateMessage} disableCollapsible />
            )}
            <div className="history-log-vert" />
          </>
        )}
      {hasContributors && (
        <Stack direction="horizontal">
          {!hideLogVert && (
            <div
              className={classNames(
                'history-log-vert',
                {
                  'history-log-vert-long': !isOpenCollapsible,
                },
              )}
            />
          )}
          {!isOpenCollapsible &&
            (
              <div
                className={classNames(
                  {
                    'ml-4 pl-1': hideLogVert,
                  },
                )}
              >
                <ContributorsAvatars contributors={contributors} />
              </div>
            )}
        </Stack>
      )}
    </div>
  );
};
